import { useTranslation } from 'next-i18next';
import { ITicketType } from '@oresundsbron/validators';
import React, { useRef } from 'react';
import { Button } from '@oresundsbron/bridge-ui';
import { RichText } from '~/components/RichText';
import { CheckmarkFilled } from '@oresundsbron/icons';

interface TicketTypeCardProps {
  item: ITicketType;
  index: number;
  onRadioChange: (index: number) => void; // Updated type
  selectedValue: ITicketType | null;
}

const TicketTypeCard: React.FC<TicketTypeCardProps> = ({
  item,
  index,
  onRadioChange,
  selectedValue,
}) => {
  const { t } = useTranslation(['onlineTicket', 'common'], {
    nsMode: 'fallback',
  });
  const dialogRef = useRef<HTMLDialogElement>(null);
  const openDialog = () => {
    dialogRef.current?.showModal();
  };
  const closeDialog = () => {
    dialogRef.current?.close();
  };
  const isCampaign: boolean = item.badgeText?.length ? true : false;
  return (
    <div
      className={`relative flex flex-col rounded-xl border-2  transition-all duration-100 ${
        isCampaign ? 'bg-blue-800 p-1.5' : ''
      }
        ${
          isCampaign && selectedValue !== item
            ? 'border-blue-800 bg-blue-800 p-1.5'
            : ''
        } ${
        selectedValue === item
          ? 'border-black outline outline-4 outline-blue-200'
          : 'border-neutral-200'
      }`}
    >
      {isCampaign && (
        <div className="px-3 pb-4 pt-3">
          <div className="font-semibold text-white">{item.badgeText}</div>
          <div className="text-xs text-blue-100">{item.campaignCondition}</div>
        </div>
      )}

      <label
        className={`group grid cursor-pointer auto-rows-auto grid-cols-[max-content_6fr_max-content] p-3 sm:p-6 ${
          isCampaign ? 'mt-auto rounded-lg bg-white' : ''
        }`}
        htmlFor={`amount-${index}`}
      >
        <span className="row-span-2 pr-3 pt-0.5 sm:pt-1.5">
          <input
            type="radio"
            name="amount"
            className="peer sr-only"
            id={`amount-${index}`}
            value={item.ticketType === 'One way ticket' ? 1 : 2}
            checked={selectedValue === item}
            onChange={() => onRadioChange(index)}
            tabIndex={0}
          />

          {selectedValue === item ? (
            <CheckmarkFilled width={20} height={20} />
          ) : (
            <div className="h-5 w-5 rounded-full border-2 border-neutral-200 bg-neutral-50 peer-hover:border-blue-200 peer-hover:bg-blue-50"></div>
          )}
        </span>
        <span>
          {item.title && (
            <span className="text-base font-semibold text-blue-950 sm:text-2xl">
              {item.title}
            </span>
          )}
        </span>
        <span className="col-span-2 col-start-2 row-start-2 pt-1.5">
          {item.shortDescription && (
            <span className="inline-block text-xs leading-relaxed text-neutral-900 sm:text-sm">
              {item.shortDescription.length > 100
                ? item.shortDescription.substring(0, 130).trim() + '…'
                : item.shortDescription}{' '}
              <button
                type="button"
                className="bg-transparent p-0 font-medium text-blue-600 underline"
                onClick={openDialog}
              >
                {t('action.readMore')}
              </button>
              <dialog
                id={`readmore-${index}`}
                className="mt-4 max-w-[calc(100%-2rem)] rounded-lg p-4 backdrop:bg-white sm:mt-auto sm:max-w-lg sm:p-6 sm:shadow-xl sm:backdrop:bg-black sm:backdrop:opacity-30"
                ref={dialogRef}
              >
                <RichText content={item.longDescription} />
                <div className="flex justify-end border-t border-neutral-200 pt-3">
                  <Button onClick={closeDialog}>{t('action.close')}</Button>
                </div>
              </dialog>
            </span>
          )}
        </span>
        <span className="col-start-3 row-start-1">
          <span className="rounded bg-neutral-200 px-2 py-1 text-xs font-semibold leading-none text-neutral-900 sm:text-sm">
            {item.ticketType === 'One way ticket'
              ? item.defaultPrice
              : item.defaultPrice * 2}
            {` ${item.currency}`}
          </span>
        </span>
      </label>
    </div>
  );
};

export default TicketTypeCard;
